import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { TrucksService } from 'src/app/trucks.service';
import { Order, OrderDetailsUpdate } from '~lmo/models/order.model';
import { LmoOrdersService } from '~lmo/services';
import { ConfigValuesService } from '~services/config-values.service';
import { HelperService } from '~services/helper.service';
import { UserService } from '~services/user.service';
import { trackById } from '~utilities/trackById';
import { ConfirmActionDialogComponent } from '../../../ui-components/confirm-action-dialog/confirm-action-dialog.component';
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-order-details-edit',
  styleUrls: ['./lmo-order-details-edit.component.scss'],
  templateUrl: './lmo-order-details-edit.component.html',
})
export class LmoOrderDetailsEditComponent implements OnInit {
  public submitting$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public submitting$ = this.submitting$$.asObservable();
  public currentOrder$: Observable<Order>;
  public editForm: FormGroup;
  public maxWeight: number;
  public togglingLockData$$ = new BehaviorSubject<boolean>(false);
  public isDispatcherOrSandCoordinator: boolean;
  public get boxes(): FormArray {
    return this.editForm.get('boxes') as FormArray;
  }
  public loaderDataLocked: boolean;
  public trackById = trackById;
  public orderCompleted = false;

  constructor(
    private orderService: LmoOrdersService,
    private fb: FormBuilder,
    public truckService: TrucksService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private configService: ConfigValuesService,
    public helperService: HelperService,
    private matDialog: MatDialog,
    private userService: UserService,
  ) {}

  public ngOnInit() {
    this.isDispatcherOrSandCoordinator = this.userService.isDispatcher() || this.userService.isSandCoordinator();
    this.currentOrder$ = this.orderService.currentOrder$;
    this.currentOrder$
      .pipe(
        filter((currentOrder) => !!currentOrder),
        take(1),
      )
      .subscribe((order) => {
        this.loaderDataLocked = order.loaderDataLocked;
        this.editForm = this.fb.group({
          bolNumber: order.bolNumber,
          description: order.description,
          ticketNumber: order.ticketNumber,
          truckId: order && order.truck && order.truck.id,
        });
        this.orderCompleted = order.orderStatus === 'completed';
        if (order.boxes) {
          const boxes: FormArray = this.fb.array([]);
          for (const box of order.boxes) {
            const group = this.fb.group({
              actualLoadWeight: null,
              ...box,
            });
            boxes.push(group);
          }
          this.editForm.addControl('boxes', boxes);
        } else {
          this.editForm.addControl('actualLoadWeight', this.fb.control(order.actualLoadWeight));
          this.editForm.addControl('bulkStorageNumber', this.fb.control(order.bulkStorageNumber));
        }
        this.getMaxWeight(order);
      });
  }

  public getMaxWeight(order) {
    this.configService.configs$.pipe(take(1)).subscribe((configs) => {
      if (!order) {
        return (this.maxWeight = configs.silo_max);
      }
      if (order.boxes) {
        if (order.boxes.length === 1) {
          return (this.maxWeight = configs.box_max_single);
        } else {
          return (this.maxWeight = configs.box_max_double0);
        }
      }
      return (this.maxWeight = configs.silo_max);
    });
  }

  public async updateOrder() {
    if (this.editForm.invalid) {
      return;
    }
    const orderUpdates = this.editForm.value as OrderDetailsUpdate;

    const actualLoadWeightDone = !!orderUpdates.actualLoadWeight || orderUpdates.boxes;
    let weightDone = true;
    if (orderUpdates.boxes) {
      orderUpdates.boxes.forEach((box) => {
        weightDone = weightDone && !!box.actualLoadWeight;
      });
    }
    const bolUpdated = !!orderUpdates.bolNumber;

    if (
      this.userService.isNexTierAccount() &&
      !this.userService.isDispatcher() &&
      !this.userService.isSandCoordinator() &&
      !this.userService.isDefault() &&
      !this.loaderDataLocked &&
      actualLoadWeightDone &&
      weightDone &&
      bolUpdated &&
      this.orderCompleted
    ) {
      const confirmDialogRef = this.matDialog.open(ConfirmActionDialogComponent, {
        data: {
          cancelButtonText: `Don't Save`,
          desc: 'Are you sure? The BOL and Load Weight will no longer be editable.',
          submitButtonText: 'Save',
          title: 'Confirm Changes',
        },
      });
      const keepGoing = await confirmDialogRef.afterClosed().toPromise();
      if (!keepGoing) {
        return;
      }
    }

    this.submitting$$.next(true);
    this.orderService.updateLoad$(orderUpdates).subscribe((updated) => {
      this.submitting$$.next(false);
      if (updated) {
        this.router.navigate([], { relativeTo: this.activatedRoute, queryParams: {} });
      }
    });
  }

  public async toggleLock(orderId: number, locked: boolean, takeAction: boolean) {
    if (!takeAction || this.togglingLockData$$.value || this.isDispatcherOrSandCoordinator) {
      return;
    }
    this.togglingLockData$$.next(true);
    this.orderService.toggleOrderDataLocked$(orderId, locked).subscribe((_) => {
      this.togglingLockData$$.next(false);
    });
  }
}
